import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import { autoCreditApi } from '@/integrations/app.api/auto-credit.api';
import { dictionariesApi } from '@/integrations/app.api/dictionaries.api';
import {
  DEPENDED_DICTS_LIST,
  PRELOAD_DICT_TYPES,
  SPECIFIC_FETCH_DICT_TYPES,
  dictContentMapper,
  fillDictExtras,
  mapContentItemsToGetBy
} from '@/services/helpers/dict';
import { addProductsDictionaryMapper } from '@/services/mappers/addProductsDictionaryMapper';
import { stockbondsProductsDictionaryMapper } from '@/services/mappers/stockbondsProductsDictionaryMapper';
import { type CacheConfig } from '@/types/api';
import { type GetDictContentType, type TDict } from '@/types/dicts';

import { DictTypes } from '../constants';
import {
  type TAddProductDictionaryResponse,
  type TDictStore,
  type TProductInsuranceServerResponse
} from '../types/store';
import { createAsyncThunk } from './create-async-thunk';

export const getProductsDictionary = createAsyncThunk(
  'dict/getProductsDictionary',
  (
    cacheConfig?: Partial<CacheConfig>
  ): Promise<TAddProductDictionaryResponse[]> => {
    return dictionariesApi.getProductsDictionary({
      ignoreCache: cacheConfig?.ignoreCache ?? false
    });
  }
);

export const getProductInsurance = createAsyncThunk(
  'dict/getProductInsurance',
  (
    cacheConfig?: Partial<CacheConfig>
  ): Promise<TProductInsuranceServerResponse[]> => {
    return dictionariesApi.getProductInsurance({
      ignoreCache: cacheConfig?.ignoreCache ?? false
    });
  }
);

export const getPromosByModel = createAsyncThunk(
  'dict/getPromosByModel',
  (modelId: string) => {
    return autoCreditApi.getPromosByModel(modelId);
  }
);

export const getOptimizedDictionaries = createAsyncThunk(
  'dict/getOptimizedDictionaries',
  async (
    params?: Partial<{
      dictTypes: DictTypes[];
      updatingDicts: DictTypes[];
      cacheConfig: Partial<CacheConfig>;
    }>
  ): Promise<{ dicts: TDict; hasAllPreloads: boolean }> => {
    const {
      dictTypes: requestedDictTypes = PRELOAD_DICT_TYPES,
      updatingDicts,
      cacheConfig
    } = params || {};
    const computedCacheConfig: CacheConfig = {
      ignoreCache: !!updatingDicts && updatingDicts.length > 0,
      ...(cacheConfig ?? {})
    };

    // Если есть обновленные справочники, то игнорируем кэш
    // ---------------------------------------
    const dictsActualDates = await dictionariesApi.getDictionariesDates();
    let newUpdateDictsDate = 0;
    if (dictsActualDates && dictsActualDates.length > 0) {
      const date = dictsActualDates.sort((a, b) => {
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      })?.[0]?.date;
      if (dayjs(date).isValid()) {
        newUpdateDictsDate = new Date(date).getTime();
      }
    }

    const lastUpdateDictsDate =
      localStorage.getItem('lastUpdateDictsDate') || 0;
    if (+lastUpdateDictsDate + 90 * 60 * 1000 < newUpdateDictsDate) {
      localStorage.setItem(
        'lastUpdateDictsDate',
        Math.max(+lastUpdateDictsDate, newUpdateDictsDate).toString()
      );
      computedCacheConfig.ignoreCache = true;
    }
    // ---------------------------------------

    const dictContentMap = new Map<
      DictTypes,
      GetDictContentType<DictTypes>[]
    >();

    // Отделяем специфичные справочники
    const computedSpecificDictTypes: DictTypes[] = [];
    const computedBulkDictTypes: DictTypes[] = [];
    const computedUpdatingDictTypes: DictTypes[] = [...(updatingDicts ?? [])];
    requestedDictTypes.forEach((requestItem) => {
      if (SPECIFIC_FETCH_DICT_TYPES.some((item) => item === requestItem)) {
        computedSpecificDictTypes.push(requestItem);
      } else {
        computedBulkDictTypes.push(requestItem);
      }
    });

    // Добавляем зависимые справочники от mainDictType, если их нет
    DEPENDED_DICTS_LIST.forEach(([mainDictType, dependDictTypes]) => {
      if (requestedDictTypes.includes(mainDictType)) {
        dependDictTypes.forEach((dependDictType) => {
          if (
            SPECIFIC_FETCH_DICT_TYPES.some((item) => item === dependDictType)
          ) {
            if (!computedSpecificDictTypes.includes(dependDictType)) {
              computedSpecificDictTypes.push(dependDictType);
            }
          } else {
            if (!computedBulkDictTypes.includes(dependDictType)) {
              computedBulkDictTypes.push(dependDictType);
            }
          }
        });
      }

      if (updatingDicts && updatingDicts.includes(mainDictType)) {
        dependDictTypes.forEach((dependDictType) => {
          if (!computedUpdatingDictTypes.includes(dependDictType)) {
            computedUpdatingDictTypes.push(dependDictType);
          }
        });
      }
    });

    // Получаем справочники через bulk
    if (computedBulkDictTypes.length > 0) {
      const responseDicts = await dictionariesApi.getOptimizedDictionaries(
        computedBulkDictTypes,
        computedUpdatingDictTypes,
        computedCacheConfig
      );
      responseDicts.forEach((responseDict) => {
        dictContentMap.set(responseDict.name, responseDict.content);
      });
    }

    // Получаем специфичные справочники
    for (const item of SPECIFIC_FETCH_DICT_TYPES) {
      if (!computedSpecificDictTypes.includes(item)) continue;

      const fetchFunction = {
        [DictTypes.Cities]: async () => {
          const content = await dictionariesApi.getCitiesForFilter(
            'all',
            computedCacheConfig
          );
          dictContentMap.set(item, content);
        },
        [DictTypes.DealersCities]: async () => {
          const content = await dictionariesApi.getCitiesForFilter(
            'dealers',
            computedCacheConfig
          );
          dictContentMap.set(item, content);
        }
      } satisfies Record<
        (typeof SPECIFIC_FETCH_DICT_TYPES)[number],
        () => Promise<void>
      >;

      if (fetchFunction[item]) {
        await fetchFunction[item]();
      }
    }

    const initialDict: Partial<TDict> = {};
    const allFetchDictTypes: DictTypes[] = [
      ...computedBulkDictTypes,
      ...computedSpecificDictTypes
    ];
    allFetchDictTypes.forEach((dictType) => {
      // Заполняем справочник
      dictContentMapper(dictType, dictContentMap, initialDict);
      // Заполняем getBy
      mapContentItemsToGetBy(initialDict[dictType]);
    });
    const dicts = initialDict as TDict;

    // Заполняем кастомные поля
    DEPENDED_DICTS_LIST.forEach(([mainDictType]) => {
      if (
        computedBulkDictTypes.includes(mainDictType) ||
        computedSpecificDictTypes.includes(mainDictType)
      ) {
        fillDictExtras(mainDictType, dicts);
      }
    });

    const hasAllPreloads = PRELOAD_DICT_TYPES.every((item) =>
      allFetchDictTypes.includes(item)
    );

    return { dicts, hasAllPreloads };
  }
);

const initialState: TDictStore = {
  dicts: null,
  additionalProductsDict: null,
  stockBoundsProductsDict: null,
  isPreloaded: false,
  addProductsInsurances: null,
  updatingDicts: undefined
};

export const dictSlice = createSlice({
  name: 'dict',
  initialState: initialState,
  reducers: {
    setProductDictsToInitial: (state) => {
      state.stockBoundsProductsDict = null;
    },
    setUpdateDicts: (state, action: PayloadAction<DictTypes[] | undefined>) => {
      state.updatingDicts = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOptimizedDictionaries.fulfilled, (state, action) => {
        state.isPreloaded = state.isPreloaded || action.payload.hasAllPreloads;
        if (!state.dicts) {
          state.dicts = action.payload.dicts;
        } else {
          state.dicts = { ...state.dicts, ...action.payload.dicts };
        }
      })
      .addCase(
        getProductsDictionary.fulfilled,
        (state, action: PayloadAction<TAddProductDictionaryResponse[]>) => {
          state.additionalProductsDict = addProductsDictionaryMapper(
            action.payload
          ).filter((product) => product.productType === 'Доп. продукт');
        }
      )
      .addCase(getPromosByModel.fulfilled, (state, action) => {
        state.stockBoundsProductsDict = stockbondsProductsDictionaryMapper(
          action.payload
        ).filter((item) => item.uuid);
      })

      .addCase(
        getProductInsurance.fulfilled,
        (state, action: PayloadAction<TProductInsuranceServerResponse[]>) => {
          state.addProductsInsurances = action.payload;
        }
      );
  }
});

export const { setProductDictsToInitial, setUpdateDicts } = dictSlice.actions;
